import { FC } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { routes } from "../../App";
import { AgentStateDto, isOnCall } from "../../models/agent";
import { BusinessUnit, useGetAgentStateQuery, useGetBusinessUnitsQuery } from "../../redux/apis/blenderApi";
import ActiveCallScreen from "../presenters/ActiveCallScreen";
import AppConflictScreen from "../presenters/AppConflictScreen";
import ErrorWithRetry from "../presenters/ErrorWithRetry";
import FullScreenSpinner from "../presenters/FullScreenSpinner";

const AGENT_STATE_POLLING_INTERVAL = 30_000;

const AgentStateGate: FC = () => {
  const { pathname } = useLocation();

  const { data: agentState, isError, refetch } = useGetAgentStateQuery(undefined, {
    pollingInterval: AGENT_STATE_POLLING_INTERVAL,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const isLoggedIn = !!agentState && agentState.state !== "LoggedOut";
  const justLoggedIn = isLoggedIn && pathname === routes.loggedOut;

  const { data: businessUnits, isLoading: isBusinessUnitsLoading } = useGetBusinessUnitsQuery(undefined, {
    skip: !justLoggedIn || !agentState?.allowBusinessUnitSelection,
  });

  if (!agentState) {
    return isError
      ? <ErrorWithRetry title="Failed to load your current state" onRetry={() => void refetch()} />
      : <FullScreenSpinner />;
  }

  if (agentState.sessionType === "Desktop")
    return <AppConflictScreen onRefresh={() => void refetch()} />;

  if (agentState.state === "LoggedOut")
    return pathname === routes.loggedOut
      ? <Outlet />
      : <Navigate to={routes.loggedOut} replace />;

  if (justLoggedIn) {
    return isBusinessUnitsLoading
      ? <FullScreenSpinner />
      : <Navigate to={getLandingRoute(agentState, businessUnits)} replace />;
  }

  if (pathname === routes.root)
    return <Navigate to={routes.home} replace />;

  if (isOnCall(agentState.state))
    return <ActiveCallScreen stateSince={agentState.stateSince} />;

  return <Outlet />;
};

export default AgentStateGate;

function getLandingRoute(agentState: AgentStateDto, businessUnits: BusinessUnit[] | undefined): string {
  if (agentState.allowBusinessUnitSelection && !(businessUnits && businessUnits.length < 2))
    return routes.businessUnitsLogin;

  return routes.home;
}
